/* ============================================================================
   <rtb-header> — behaviour for sections/rtb-header.liquid.

   Load it with `defer` (classic script, not a module):
       <script src="{{ 'rtb-header.js' | asset_url }}" defer></script>

   Needs assets/rtb-a11y.js (window.RTB.a11y), which layout/theme.liquid loads
   deferred in the head, so it has always run by the time this does.

   RELATIONSHIP TO HORIZON
   The element extends HTMLElement, not Horizon's Component. It reuses Horizon
   where Horizon owns the state: the scroll lock (lockScroll/unlockScroll from
   @theme/utilities, loaded with a dynamic import through the import map), the
   cart count (<cart-icon>) and the cart drawer's open/close events.

   WHY A CUSTOM ELEMENT
   The theme editor re-renders a section's HTML in place on every edit.
   connectedCallback / disconnectedCallback give setup and teardown for free,
   and every listener added below is removed again on disconnect, including
   those on `document` and `window` and the matchMedia subscription.

   JOBS
   1. Drop-downs. Each panel is a server-rendered list of links; one is open at
      a time. A mouse opens it on hover after a short dwell (desktop only);
      keyboard and touch open it on click. A panel the pointer opened stays
      open when the mouse then clicks its trigger. Escape closes it from
      anywhere on the page while it is open (WCAG 1.4.13), returning focus to
      the trigger when focus was inside the header. Tabbing out closes it. The
      desktop card is placed under its trigger (--rtb-panel-start/-top).
   2. Search. Shares the desktop drop-down slot with the panels and excludes
      them, but outlives the pointer: it was opened by a click and closes on a
      second click, on Escape, or when a panel takes the slot. Below 990px the
      field is always present in the drawer and the toggle is hidden.
   3. Mobile drawer. A modal overlay: dialog semantics while open, focus
      trapped, the page behind it `inert`, the scroll locked through Horizon,
      and focus returned to the burger on close.
   4. --header-height / --header-group-height on <body>. Horizon measures these
      in its own header's script, which does not run for <rtb-header>.
   5. Condensed state. CSS makes the header sticky (`sticky="always"`); this
      file only decides WHEN the band condenses, by writing
      `data-sticky-state="active"` with hysteresis.
   6. Cart feedback. Keeps the bag's visually hidden name in sync with the
      count and plays the shake, only after a cart update succeeds.
   7. Logo ornament. Swings with the visitor's scrolling and replays its swing
      on pointer enter; nothing moves under prefers-reduced-motion.

   EXPECTED MARKUP (all rendered by the section; no markup is created here)
     [data-rtb-header-item] > [data-rtb-header-trigger][aria-controls] + [data-rtb-header-panel][hidden]
     [data-rtb-header-burger], [data-rtb-header-close]
     [data-rtb-header-drawer][data-drawer-label]
     [data-rtb-header-search-toggle], [data-rtb-header-search-field]
     [data-rtb-header-cart], [data-rtb-header-cart-label][data-label-one][data-label-many]
   ============================================================================ */

(function () {
  'use strict';

  if (customElements.get('rtb-header')) return;

  var a11y = window.RTB.a11y;

  var DESKTOP = '(min-width: 990px)';

  /* --- Where the band condenses ------------------------------------------

     The condense point is measured from a SENTINEL: the element whose bottom
     edge marks the first band having passed under the header. A section may
     name its own with `data-rtb-header-sentinel`; otherwise the first band is
     found by walking down from the first child of #MainContent while that
     child is taller than the viewport, at most SENTINEL_MAX_DEPTH levels.

     Clamps keep a wrong guess harmless: never before the header has scrolled
     its own height away, never beyond one viewport. With nothing measurable
     FALLBACK_STICK applies. RELEASE_RATIO is the design's hysteresis, 110/180:
     a single threshold sits exactly where the state change moves the page, so
     the band would chatter. */
  var SENTINEL_SELECTOR = '[data-rtb-header-sentinel]';
  var FALLBACK_SELECTOR = '#MainContent';
  var SENTINEL_MAX_DEPTH = 3;
  var RELEASE_RATIO = 110 / 180;
  var FALLBACK_STICK = 180;
  /* Safety net for the height-settling lock: an element hidden mid-transition
     fires no `transitionend`. Comfortably longer than --rtb-dur-base. */
  var HEIGHT_SETTLE_MS = 400;

  /* StandardEvents.cartLinesUpdate from @shopify/events, as a literal because
     a classic script cannot import the module. */
  var CART_LINES_UPDATE = 'shopify:cart:lines-update';

  /* DrawerOpenEvent / DrawerCloseEvent from @theme/theme-drawer, as literals
     for the same reason. They bubble to the document; Horizon's own header
     syncs the bag's aria-expanded off exactly these two. */
  var DRAWER_OPEN = 'theme-drawer:open';
  var DRAWER_CLOSE = 'theme-drawer:close';

  /* Long enough for the 720ms shake keyframe. A timer, not `animationend`,
     because under reduced motion the animation is `none` and that event never
     fires. */
  var SHAKE_MS = 760;
  var SHAKE_ANIMATION = 'rtb-header-bag-shake';

  /* Logo ornament swing (see _kickSwing). Per 60fps frame: stiffness is
     (2π / 80 frames)², a ~1.3s swing; damping settles it in ~2.5s; gain is
     degrees of angular velocity per scrolled px; a scroll step is capped so a
     jump (anchor link, Home/End) does not fling it. */
  var SWING_STIFFNESS = 0.00617;
  var SWING_DAMPING = 0.06;
  var SWING_GAIN = 0.004;
  var SWING_MAX_DELTA = 60;
  var SWING_MAX_ANGLE = 10;
  var SWING_REST = 0.01;

  /* Hover intent: 400ms of dwell before the pointer is believed. The delay is
     symmetric, see _scheduleIntent. */
  var HOVER_INTENT_MS = 400;

  /* Space between a trigger and its drop-down card. */
  var PANEL_GAP = 8;

  class RtbHeader extends HTMLElement {
    connectedCallback() {
      this.torndown = false;
      this.triggers = Array.from(this.querySelectorAll('[data-rtb-header-trigger]'));
      this.items = Array.from(this.querySelectorAll('[data-rtb-header-item]'));
      this.burger = this.querySelector('[data-rtb-header-burger]');
      this.closeButton = this.querySelector('[data-rtb-header-close]');
      this.searchToggle = this.querySelector('[data-rtb-header-search-toggle]');
      this.drawer = this.querySelector('[data-rtb-header-drawer]');

      this.cartControl = this.querySelector('[data-rtb-header-cart]');
      this.cartLabel = this.querySelector('[data-rtb-header-cart-label]');

      this.desktop = window.matchMedia(DESKTOP);
      this.canHover = window.matchMedia('(hover: hover)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.openTrigger = null;
      this.openedByHover = false;
      this.scrollLocked = false;
      this.inertElements = [];
      this.stuck = false;
      this.scrollFrame = 0;
      this.shakeTimer = 0;
      this.shakeFrame = 0;
      this.lastHeaderHeight = -1;
      this.lastGroupHeight = -1;

      /* The one pending hover intent, an open OR a close. Cancelled in every
         teardown path, since a stranded timer would fire against a detached
         element. */
      this.pendingOpen = null;

      /* Thresholds are layout reads, taken once and re-taken only when
         something could move the first band, never per scroll frame.
         `baseGroupHeight` is the UNCONDENSED header group height, recorded by
         _measure while the band is open, so the condense point does not move
         every time the state it decides flips. */
      this.thresholdsStale = true;
      this.stickAbove = FALLBACK_STICK;
      this.releaseBelow = FALLBACK_STICK * RELEASE_RATIO;
      this.baseGroupHeight = 0;

      /* At >=990px `.page-wrapper` is the scroll container and window.scrollY
         stays 0; below 990px the document scrolls. See _activeScroller. */
      this.scroller = document.querySelector('.page-wrapper');

      this._onTriggerClick = this._handleTriggerClick.bind(this);
      this._onTriggerEnter = this._handleTriggerEnter.bind(this);
      this._onMouseLeave = this._handleMouseLeave.bind(this);
      this._onKeydown = this._handleKeydown.bind(this);
      this._onDocumentKeydown = this._handleDocumentKeydown.bind(this);
      this._onBurger = this._handleBurger.bind(this);
      this._onLogoEnter = this._handleLogoEnter.bind(this);
      this._stepSwing = this._handleSwingFrame.bind(this);
      this._onClose = this.closeDrawer.bind(this);
      this._onSearchToggle = this._handleSearchToggle.bind(this);
      this._onBreakpoint = this._handleBreakpoint.bind(this);
      this._onDocumentClick = this._handleDocumentClick.bind(this);
      this._onScroll = this._handleScroll.bind(this);
      this._readScroll = this._handleReadScroll.bind(this);
      this._onCartUpdate = this._handleCartUpdate.bind(this);
      this._onCartDrawerState = this._handleCartDrawerState.bind(this);
      this._onResize = this._handleResize.bind(this);
      this._onSectionLoad = this._handleSectionLoad.bind(this);
      this._onBarTransition = this._handleBarTransition.bind(this);
      this._onBarSettled = this._handleBarSettled.bind(this);
      this._onPointerOver = this._handlePointerOver.bind(this);
      this._onFocusOut = this._handleFocusOut.bind(this);

      this.triggers.forEach(
        function (trigger) {
          trigger.addEventListener('click', this._onTriggerClick);
        }.bind(this)
      );

      // Hover is bound to EVERY top-level item, not only those with a panel:
      // arriving on a plain link must dismiss whatever is open.
      this.items.forEach(
        function (item) {
          item.addEventListener('mouseenter', this._onTriggerEnter);
        }.bind(this)
      );

      this.addEventListener('mouseleave', this._onMouseLeave);
      this.addEventListener('keydown', this._onKeydown);

      /* Arriving INSIDE the open panel voids any intent formed on the way in,
         see _handlePointerOver. mouseover bubbles, so it is delegated here. */
      this.addEventListener('mouseover', this._onPointerOver);
      this.addEventListener('focusout', this._onFocusOut);
      if (this.burger) this.burger.addEventListener('click', this._onBurger);

      this.logoOrnament = this.querySelector('.rtb-header__logo-ornament');
      this.swing = { angle: 0, velocity: 0, lastY: null, time: 0 };
      this.swingFrame = 0;
      if (this.logoOrnament) this.logoOrnament.parentElement.addEventListener('pointerenter', this._onLogoEnter);
      if (this.closeButton) this.closeButton.addEventListener('click', this._onClose);
      if (this.searchToggle) this.searchToggle.addEventListener('click', this._onSearchToggle);
      document.addEventListener('click', this._onDocumentClick);

      this.desktop.addEventListener('change', this._onBreakpoint);

      // Passive: this listener never calls preventDefault; it schedules one rAF.
      window.addEventListener('scroll', this._onScroll, { passive: true });
      if (this.scroller) this.scroller.addEventListener('scroll', this._onScroll, { passive: true });

      /* While the band animates its own height the page is moving under it:
         the header is in flow, so condensing shortens the document. The lock
         in _handleBarTransition holds the decision until the height lands,
         then re-reads once. */
      this.bar = this.querySelector('.rtb-header__bar');
      if (this.bar) {
        this.bar.addEventListener('transitionrun', this._onBarTransition);
        this.bar.addEventListener('transitionend', this._onBarTransition);
        this.bar.addEventListener('transitioncancel', this._onBarTransition);
      }

      // Both only INVALIDATE the condense point; the re-read happens on the
      // next rAF. A resize changes the viewport clamp; a section load (theme
      // editor, Section Rendering API) can replace the first band outright.
      window.addEventListener('resize', this._onResize, { passive: true });
      document.addEventListener('shopify:section:load', this._onSectionLoad);

      document.addEventListener(CART_LINES_UPDATE, this._onCartUpdate);
      document.addEventListener(DRAWER_OPEN, this._onCartDrawerState);
      document.addEventListener(DRAWER_CLOSE, this._onCartDrawerState);
      this._syncCartExpanded();

      // The editor can fire unload while the node is still attached, so
      // teardown is idempotent and hangs off both.
      this._offSectionUnload = a11y.onSectionUnload(this, this.disconnectedCallback.bind(this));

      this._startMeasuring();
      this._updateStickyState();
    }

    disconnectedCallback() {
      if (this.torndown) return;
      this.torndown = true;

      this.triggers.forEach(
        function (trigger) {
          trigger.removeEventListener('click', this._onTriggerClick);
        }.bind(this)
      );

      this.items.forEach(
        function (item) {
          item.removeEventListener('mouseenter', this._onTriggerEnter);
        }.bind(this)
      );

      this.removeEventListener('mouseleave', this._onMouseLeave);
      this.removeEventListener('keydown', this._onKeydown);
      this.removeEventListener('mouseover', this._onPointerOver);
      this.removeEventListener('focusout', this._onFocusOut);
      if (this.burger) this.burger.removeEventListener('click', this._onBurger);
      if (this.logoOrnament) this.logoOrnament.parentElement.removeEventListener('pointerenter', this._onLogoEnter);
      if (this.closeButton) this.closeButton.removeEventListener('click', this._onClose);
      if (this.searchToggle) this.searchToggle.removeEventListener('click', this._onSearchToggle);
      document.removeEventListener('click', this._onDocumentClick);
      document.removeEventListener('keydown', this._onDocumentKeydown);
      if (this._offSectionUnload) this._offSectionUnload();
      this._offSectionUnload = null;
      document.removeEventListener(CART_LINES_UPDATE, this._onCartUpdate);
      document.removeEventListener(DRAWER_OPEN, this._onCartDrawerState);
      document.removeEventListener(DRAWER_CLOSE, this._onCartDrawerState);

      window.removeEventListener('scroll', this._onScroll);
      if (this.scroller) this.scroller.removeEventListener('scroll', this._onScroll);
      if (this.bar) {
        this.bar.removeEventListener('transitionrun', this._onBarTransition);
        this.bar.removeEventListener('transitionend', this._onBarTransition);
        this.bar.removeEventListener('transitioncancel', this._onBarTransition);
      }
      if (this.heightSettleTimer) window.clearTimeout(this.heightSettleTimer);
      this.heightSettleTimer = 0;
      this.heightSettling = false;
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('shopify:section:load', this._onSectionLoad);

      if (this.scrollFrame) window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = 0;
      if (this.swingFrame) window.cancelAnimationFrame(this.swingFrame);
      this.swingFrame = 0;
      if (this.shakeFrame) window.cancelAnimationFrame(this.shakeFrame);
      this.shakeFrame = 0;
      if (this.shakeTimer) window.clearTimeout(this.shakeTimer);
      this.shakeTimer = 0;

      this._cancelPendingOpen();

      if (this.desktop) this.desktop.removeEventListener('change', this._onBreakpoint);

      if (this.observer) this.observer.disconnect();
      this.observer = null;

      // Never leave the page locked or inert behind a removed drawer.
      this._setScrollLock(false);
      this._setPageInert(false);

      this.triggers = [];
      this.items = [];
      this.openTrigger = null;
      this.cartControl = null;
      this.cartLabel = null;
    }

    /* Replays only the swing, never the entrance, and only once it has
       finished, so re-entering mid-swing does not restart it. Reduced motion
       has no animation to find, so this is a no-op there. */
    _handleLogoEnter() {
      if (!this.logoOrnament || typeof this.logoOrnament.getAnimations !== 'function') return;
      this.logoOrnament.getAnimations().forEach(function (animation) {
        if (animation.animationName !== 'rtb-logo-ornament-swing' || animation.playState !== 'finished') return;
        animation.currentTime = animation.effect.getTiming().delay;
        animation.play();
      });
    }

    /* --- Scroll lock and inert page ---------------------------------------

       Horizon's lockScroll/unlockScroll keep a set of owners and clear
       `html[scroll-lock]` only when the set empties, so the drawer and the
       cart drawer can overlap without unlocking each other. */

    _setScrollLock(locked) {
      if (this.scrollLocked === locked) return;
      this.scrollLocked = locked;

      var owner = this;
      import('@theme/utilities')
        .then(function (utilities) {
          if (locked) utilities.lockScroll(owner);
          else utilities.unlockScroll(owner);
        })
        .catch(function () {
          // Without the module the page simply scrolls behind the drawer.
        });
    }

    /* The page behind the modal drawer is inert, so neither a screen reader's
       virtual cursor nor a pointer can reach it. Only elements this call made
       inert are released again. */
    _setPageInert(inert) {
      if (!inert) {
        this.inertElements.forEach(function (element) {
          element.inert = false;
        });
        this.inertElements = [];
        return;
      }

      var targets = [document.getElementById('MainContent'), document.querySelector('#MainContent ~ footer')];
      this.inertElements = targets.filter(function (element) {
        return element && !element.inert;
      });
      this.inertElements.forEach(function (element) {
        element.inert = true;
      });
    }

    /* --- Drop-downs ------------------------------------------------------- */

    panelFor(trigger) {
      var id = trigger.getAttribute('aria-controls');
      return id ? this.querySelector('#' + CSS.escape(id)) : null;
    }

    /* --- Hover intent -----------------------------------------------------

       THE DELAY IS SYMMETRIC, so one timer carries both verbs. Hover is bound
       to every item, and a delayed open with an immediate close would close
       the open panel at once and reopen it 400ms later as the pointer crosses
       the row. A null trigger therefore schedules the CLOSE on the same clock.
       Leaving the header entirely still closes at once. */

    _scheduleIntent(trigger) {
      this._cancelPendingOpen();
      this.pendingOpen = window.setTimeout(
        function () {
          this.pendingOpen = null;
          if (trigger) this.openPanel(trigger, true);
          else this.closePanels();
        }.bind(this),
        HOVER_INTENT_MS
      );
    }

    _cancelPendingOpen() {
      if (this.pendingOpen) {
        window.clearTimeout(this.pendingOpen);
        this.pendingOpen = null;
      }
    }

    openPanel(trigger, viaHover) {
      // Any open supersedes a pending intent, which must never fire later.
      this._cancelPendingOpen();

      if (this.openTrigger === trigger) return;
      this.closePanels();
      this.closeSearch();

      var panel = this.panelFor(trigger);
      if (!panel) return;

      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      this._placePanel(trigger, panel);
      this.openTrigger = trigger;
      this.openedByHover = !!viaHover;
      document.addEventListener('keydown', this._onDocumentKeydown);
    }

    closePanels() {
      this.triggers.forEach(
        function (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          var panel = this.panelFor(trigger);
          if (panel) panel.hidden = true;
        }.bind(this)
      );
      this.openTrigger = null;
      this.openedByHover = false;
      document.removeEventListener('keydown', this._onDocumentKeydown);
    }

    /* The desktop card hangs from the band and is only as wide as its list,
       so its place is measured: the trigger's inline-start edge, clamped so
       the card never crosses the band's inline-end edge minus the gutter.
       Below 990px the drawer list is static and CSS ignores the variables. */
    _placePanel(trigger, panel) {
      if (!this.desktop.matches) return;
      var box = panel.offsetParent;
      if (!box) return;

      var boxRect = box.getBoundingClientRect();
      var triggerRect = trigger.getBoundingClientRect();
      var gutter = parseFloat(getComputedStyle(this).getPropertyValue('--rtb-gutter')) || 16;

      /* Measured from the containing block's INLINE-START edge (its right
         edge in RTL), so CSS reads it through inset-inline-start. */
      var rtl = getComputedStyle(box).direction === 'rtl';
      var start = rtl ? boxRect.right - triggerRect.right : triggerRect.left - boxRect.left;
      var maxStart = boxRect.width - panel.offsetWidth - gutter;
      start = Math.max(0, Math.min(start, maxStart));
      panel.style.setProperty('--rtb-panel-start', Math.round(start) + 'px');
      // Just under the trigger, not at the band's bottom edge.
      panel.style.setProperty('--rtb-panel-top', Math.round(triggerRect.bottom - boxRect.top + PANEL_GAP) + 'px');
    }

    /* Re-places the open card. The trigger moves whenever the band's width
       changes, including when the cart drawer squeezes the page without a
       resize event, so both the resize and the ResizeObserver paths call this. */
    _placeOpenPanel() {
      if (!this.openTrigger) return;
      var panel = this.panelFor(this.openTrigger);
      if (panel && !panel.hidden) this._placePanel(this.openTrigger, panel);
    }

    _handleTriggerClick(event) {
      var trigger = event.currentTarget;
      this._cancelPendingOpen();

      if (this.openTrigger !== trigger) {
        this.openPanel(trigger);
        return;
      }

      // The pointer opened this panel a moment ago; a mouse click on its
      // trigger confirms it rather than closing it under the cursor. The next
      // click closes it.
      if (this.openedByHover && event.pointerType === 'mouse') {
        this.openedByHover = false;
        return;
      }

      this.closePanels();
    }

    _handleTriggerEnter(event) {
      // Hover-to-open is a desktop, hover-capable affordance only: on touch the
      // synthesised mouseenter would open a panel nobody asked for.
      if (!this.desktop.matches || !this.canHover.matches) return;

      var trigger = event.currentTarget.querySelector('[data-rtb-header-trigger]');

      // Back on the item that owns what is open: any intent formed on the way
      // here is void.
      if (trigger && this.openTrigger === trigger) {
        this._cancelPendingOpen();
        return;
      }

      this._scheduleIntent(trigger || null);
    }

    _handleMouseLeave() {
      this._cancelPendingOpen();
      if (!this.desktop.matches) return;
      this.closePanels();
    }

    /* Moving diagonally from a trigger into its card can brush a neighbouring
       item and schedule an intent. Arriving inside the open card proves the
       neighbour was passed through, not chosen, so the intent is voided. */
    _handlePointerOver(event) {
      if (!this.pendingOpen || !this.openTrigger) return;

      var target = event.target;
      if (!(target instanceof Element)) return;

      var panel = target.closest('[data-rtb-header-panel]');
      if (panel && panel === this.panelFor(this.openTrigger)) this._cancelPendingOpen();
    }

    /* --- Search ----------------------------------------------------------- */

    closeSearch() {
      this.removeAttribute('data-search-open');
      if (this.searchToggle) this.searchToggle.setAttribute('aria-expanded', 'false');
    }

    _handleSearchToggle() {
      this._cancelPendingOpen();

      if (this.hasAttribute('data-search-open')) {
        this.closeSearch();
        return;
      }

      // The cancel above keeps a pending panel open from taking the slot out
      // from under the focused field.
      this.closePanels();
      this.setAttribute('data-search-open', '');
      if (this.searchToggle) this.searchToggle.setAttribute('aria-expanded', 'true');

      var field = this.querySelector('[data-rtb-header-search-field]');
      if (field) field.focus();
    }

    /* --- Mobile drawer ---------------------------------------------------- */

    openDrawer() {
      if (!this.drawer || !this.burger) return;

      this.returnFocusTo = document.activeElement;
      this.setAttribute('data-drawer-open', '');
      this.burger.setAttribute('aria-expanded', 'true');
      this._setScrollLock(true);
      this._setPageInert(true);

      // Dialog semantics are applied here, not in the markup: the same element
      // is the static desktop nav row above 990px. The name comes from the
      // markup so it stays translatable.
      a11y.setDialog(this.drawer, this.drawer.getAttribute('data-drawer-label'));

      var first = this.closeButton || this.drawer.querySelector(a11y.FOCUSABLE);
      if (first) first.focus();
    }

    closeDrawer() {
      if (!this.hasAttribute('data-drawer-open')) return;

      this.removeAttribute('data-drawer-open');
      a11y.clearDialog(this.drawer);
      if (this.burger) this.burger.setAttribute('aria-expanded', 'false');
      this._setPageInert(false);
      this._setScrollLock(false);
      this.closePanels();

      a11y.returnFocus(this.returnFocusTo, this.burger);
      this.returnFocusTo = null;
    }

    _handleBurger() {
      if (this.hasAttribute('data-drawer-open')) this.closeDrawer();
      else this.openDrawer();
    }

    /* --- Keyboard and outside click --------------------------------------- */

    _handleKeydown(event) {
      if (event.key === 'Tab' && this.hasAttribute('data-drawer-open')) {
        a11y.trapFocus(this.drawer, event);
        return;
      }

      if (event.key !== 'Escape') return;

      // An open panel is closed by the document listener, which sees Escape
      // wherever focus is. It must win over the search and the drawer.
      if (this.openTrigger) return;

      if (this.hasAttribute('data-search-open')) {
        this.closeSearch();
        if (this.searchToggle) this.searchToggle.focus();
        return;
      }

      if (this.hasAttribute('data-drawer-open')) this.closeDrawer();
    }

    /* Bound only while a panel is open. A card opened by hover can be
       dismissed with Escape even when focus is elsewhere on the page
       (WCAG 1.4.13). Focus returns to the trigger only when it was inside the
       header; it is never pulled from elsewhere. */
    _handleDocumentKeydown(event) {
      if (event.key !== 'Escape' || !this.openTrigger) return;

      var trigger = this.openTrigger;
      var focusInside = this.contains(document.activeElement);
      this.closePanels();
      if (focusInside) trigger.focus();
    }

    _handleDocumentClick(event) {
      if (this.contains(event.target)) return;
      this._cancelPendingOpen();
      this.closePanels();
      this.closeSearch();
    }

    /* Keyboard users leave a drop-down by tabbing out of it, and the card
       closes behind them.

       A NULL relatedTarget IS IGNORED: Safari does not focus a clicked link or
       button, so a mouse click on a row blurs the trigger with nowhere to go,
       and closing then would hide the card before the click lands. Click
       outside is handled by _handleDocumentClick. Desktop only: the drawer has
       its own focus trap. */
    _handleFocusOut(event) {
      if (!this.desktop.matches || !this.openTrigger) return;
      var next = event.relatedTarget;
      if (!next) return;

      var item = this.openTrigger.closest('[data-rtb-header-item]');
      if (item && item.contains(next)) return;
      this.closePanels();
    }

    _handleBreakpoint() {
      // The layout changed under an open overlay: reset to a known state
      // rather than leave a fixed drawer stranded on a desktop viewport.
      this._cancelPendingOpen();
      this.closePanels();
      this.closeSearch();
      if (this.desktop.matches) this.closeDrawer();

      // The scroll container swaps at this breakpoint, so the condense
      // decision and its thresholds are re-taken, and the height-settling lock
      // (which guards a page mid-animation) is released.
      if (this.heightSettleTimer) window.clearTimeout(this.heightSettleTimer);
      this.heightSettleTimer = 0;
      this.heightSettling = false;
      this.thresholdsStale = true;
      this._updateStickyState();
    }

    _handleResize() {
      this._placeOpenPanel();
      this.thresholdsStale = true;
      this._handleScroll();
    }

    _handleSectionLoad() {
      this.thresholdsStale = true;
      this._handleScroll();
    }

    /* --- Condensed state --------------------------------------------------

       This does not make the header sticky; `sticky="always"` in the markup
       does. It toggles `data-sticky-state="active"`, the attribute Horizon's
       own header writes, which snippets/product-information-content.liquid
       keys its sticky offset on. */

    /* The element that is actually scrolling, chosen by whether it CAN
       scroll: `.page-wrapper` from 990px up, the document below. */
    _activeScroller() {
      var el = this.scroller;
      if (el && el.scrollHeight > el.clientHeight) return el;
      return null;
    }

    /* One coordinate space: the scrolling container's own offset, including
       when it is legitimately 0. The window can be scrolled programmatically
       (focus, anchors, scrollIntoView) even under `overflow: hidden`, so both
       offsets can be non-zero at once and must not be mixed. */
    _scrollTop() {
      var el = this._activeScroller();
      if (el) return el.scrollTop || 0;
      return window.scrollY || window.pageYOffset || 0;
    }

    _handleScroll() {
      // At most one read per frame: scrollTop is a layout read.
      if (this.scrollFrame) return;
      this.scrollFrame = window.requestAnimationFrame(this._readScroll);
    }

    _handleReadScroll() {
      this.scrollFrame = 0;
      this._updateStickyState();
      this._kickSwing();
    }

    /* THE ORNAMENT HANGS FROM THE WORDMARK AND SCROLLING SWINGS IT. Each scroll
       frame adds the distance scrolled as angular velocity, and a damped
       spring pulls it back upright, tuned to the load animation's own swing so
       the two read as one object. It writes `transform`, which composes with
       the CSS `rotate`/`scale` of the load animation, and the frame loop runs
       only while the ornament moves. None of it under reduced motion. */
    _kickSwing() {
      if (!this.logoOrnament || this.reducedMotion.matches) return;
      var y = this._scrollTop();
      var swing = this.swing;
      if (swing.lastY !== null) {
        var delta = Math.max(-SWING_MAX_DELTA, Math.min(SWING_MAX_DELTA, y - swing.lastY));
        swing.velocity -= delta * SWING_GAIN;
      }
      swing.lastY = y;
      if (!this.swingFrame && Math.abs(swing.velocity) > SWING_REST) {
        swing.time = 0;
        this.swingFrame = window.requestAnimationFrame(this._stepSwing);
      }
    }

    _handleSwingFrame(now) {
      var swing = this.swing;
      var step = swing.time ? Math.min((now - swing.time) / 16.667, 3) : 1;
      swing.time = now;

      swing.velocity += (-SWING_STIFFNESS * swing.angle - SWING_DAMPING * swing.velocity) * step;
      swing.angle = Math.max(-SWING_MAX_ANGLE, Math.min(SWING_MAX_ANGLE, swing.angle + swing.velocity * step));

      if (Math.abs(swing.angle) < SWING_REST && Math.abs(swing.velocity) < SWING_REST) {
        swing.angle = 0;
        swing.velocity = 0;
        this.logoOrnament.style.transform = '';
        this.swingFrame = 0;
        return;
      }

      this.logoOrnament.style.transform = 'rotate(' + swing.angle.toFixed(2) + 'deg)';
      this.swingFrame = window.requestAnimationFrame(this._stepSwing);
    }

    /* The element whose bottom edge is the condense point. `firstElementChild`
       skips the text nodes and comments a Liquid-rendered document is full of. */
    _sentinel() {
      var explicit = document.querySelector(SENTINEL_SELECTOR);
      if (explicit) return explicit;

      var main = document.querySelector(FALLBACK_SELECTOR);
      if (!main) return null;

      var node = main.firstElementChild;
      var viewport = window.innerHeight || 0;

      for (var depth = 0; node && viewport && depth < SENTINEL_MAX_DEPTH; depth++) {
        if (node.getBoundingClientRect().height <= viewport) break;
        var child = node.firstElementChild;
        if (!child) break;
        node = child;
      }

      return node;
    }

    _measureThresholds() {
      this.thresholdsStale = false;

      var floor = this.baseGroupHeight || 0;
      var ceiling = window.innerHeight || 0;
      var stick = 0;

      var sentinel = this._sentinel();
      if (sentinel) {
        // The band's bottom edge in the scroller's own content coordinates,
        // minus the header it scrolls under: the distance travelled before the
        // band has fully passed.
        stick = sentinel.getBoundingClientRect().bottom + this._scrollTop() - floor;
      }

      if (!(stick > 0)) stick = FALLBACK_STICK;
      if (stick < floor) stick = floor;
      if (ceiling && stick > ceiling) stick = ceiling;

      this.stickAbove = stick;
      this.releaseBelow = stick * RELEASE_RATIO;
    }

    /* Only the band's own height matters; a colour or shadow transition says
       nothing about the page moving. */
    _handleBarTransition(event) {
      if (event.propertyName !== 'height') return;

      if (event.type === 'transitionrun') {
        this.heightSettling = true;
        if (this.heightSettleTimer) window.clearTimeout(this.heightSettleTimer);
        this.heightSettleTimer = window.setTimeout(this._onBarSettled, HEIGHT_SETTLE_MS);
        return;
      }

      this._handleBarSettled();
    }

    /* One re-read after the height lands, so a scroll during the animation is
       decided once, against a page that has stopped moving. */
    _handleBarSettled() {
      if (this.heightSettleTimer) window.clearTimeout(this.heightSettleTimer);
      this.heightSettleTimer = 0;
      if (!this.heightSettling) return;
      this.heightSettling = false;
      this._handleScroll();
    }

    _updateStickyState() {
      // Reduced motion runs no transition and never takes this lock.
      if (this.heightSettling) return;

      if (this.thresholdsStale) this._measureThresholds();

      var y = this._scrollTop();
      var stuck = this.stuck ? y > this.releaseBelow : y > this.stickAbove;
      if (stuck === this.stuck) return;

      this.stuck = stuck;
      if (stuck) this.setAttribute('data-sticky-state', 'active');
      else this.removeAttribute('data-sticky-state');

      // The ResizeObserver catches the transition's frames; this covers
      // reduced motion, where there are none.
      this._measure();
    }

    /* --- Cart feedback -----------------------------------------------------

       Horizon's <cart-icon> owns the bubble and its count. This file owns the
       bag's accessible name (the bubble's numerals are aria-hidden) and the
       shake. Both wait for the event's `promise`, which Horizon resolves once
       the cart has been re-fetched; a rejected add also resolves, with
       `detail.didError: true`, and that flag gates both.

       CartLinesUpdateEvent is not a CustomEvent: the payload sits on the event
       instance, not on `.detail`. */

    /* The bag trigger carries `aria-haspopup="dialog"` and `aria-controls`, so
       its `aria-expanded` has to track the cart drawer. No-op on the `<a href>`
       branch, which carries no aria-expanded. */
    _handleCartDrawerState(event) {
      var target = event && event.target;
      if (!target || target.id !== 'cart-drawer') return;
      this._syncCartExpanded();
    }

    _syncCartExpanded() {
      if (!this.cartControl || !this.cartControl.hasAttribute('aria-expanded')) return;

      var drawer = document.getElementById('cart-drawer');
      if (!drawer) return;

      this.cartControl.setAttribute('aria-expanded', drawer.hasAttribute('open') ? 'true' : 'false');
    }

    _handleCartUpdate(event) {
      var self = this;
      var promise = event && event.promise;
      var added = !!(event && event.action === 'add');

      if (promise && typeof promise.then === 'function') {
        promise
          .then(function (result) {
            self._applyCartUpdate(result, added);
          })
          .catch(function () {
            // A rejected add is not a cart change; cart-icon.js reports it.
          });
        return;
      }

      this._applyCartUpdate(null, added);
    }

    _applyCartUpdate(result, added) {
      // A 422 resolves too, carrying the unchanged cart.
      if (result && result.detail && result.detail.didError) return;

      // Only the cart's own total is a total: `detail.itemCount` is the
      // quantity this add asked for, and a wrong number here is spoken aloud.
      var count = null;
      if (result && result.cart && typeof result.cart.totalQuantity === 'number') count = result.cart.totalQuantity;

      var text = count === null ? null : this._cartLabelText(count);
      if (this.cartLabel && text) this.cartLabel.textContent = text;

      if (added) this._shakeCart();
    }

    /* Both plural forms come from the label node (data-label-one /
       data-label-many, with a [count] placeholder), rendered from the locale
       file. Without them the label is left as the server rendered it. */
    _cartLabelText(count) {
      if (!this.cartLabel) return null;
      var template = this.cartLabel.getAttribute(count === 1 ? 'data-label-one' : 'data-label-many');
      return template ? template.replace('[count]', String(count)) : null;
    }

    /* Restart rather than stack: a second add mid-shake replays it from the
       start through the running CSS animation, with no forced reflow. */
    _shakeCart() {
      var control = this.cartControl;
      if (!control) return;

      var running = [];
      if (control.hasAttribute('data-rtb-shake') && typeof control.getAnimations === 'function') {
        running = control.getAnimations().filter(function (animation) {
          return animation.animationName === SHAKE_ANIMATION;
        });
      }

      if (running.length) {
        running.forEach(function (animation) {
          animation.currentTime = 0;
          animation.play();
        });
      } else if (control.hasAttribute('data-rtb-shake')) {
        // Finished but not yet cleared: re-add it on a later frame, after the
        // removal has been styled.
        control.removeAttribute('data-rtb-shake');
        if (this.shakeFrame) window.cancelAnimationFrame(this.shakeFrame);
        this.shakeFrame = window.requestAnimationFrame(
          function () {
            this.shakeFrame = window.requestAnimationFrame(
              function () {
                this.shakeFrame = 0;
                if (this.cartControl) this.cartControl.setAttribute('data-rtb-shake', '');
              }.bind(this)
            );
          }.bind(this)
        );
      } else {
        control.setAttribute('data-rtb-shake', '');
      }

      if (this.shakeTimer) window.clearTimeout(this.shakeTimer);
      this.shakeTimer = window.setTimeout(
        function () {
          this.shakeTimer = 0;
          if (this.cartControl) this.cartControl.removeAttribute('data-rtb-shake');
        }.bind(this),
        SHAKE_MS
      );
    }

    /* --- Header height measurement ---------------------------------------- */

    _startMeasuring() {
      this._measure();

      if (typeof ResizeObserver !== 'function') return;
      this.observer = new ResizeObserver(
        function () {
          this._measure();
          this._placeOpenPanel();
        }.bind(this)
      );
      this.observer.observe(this);
    }

    /* Writes to <body> only when a value changes: the observer fires on every
       frame of the condense transition, and each write restyles the page. */
    _measure() {
      var group = document.getElementById('header-group');
      if (!group) return;

      var height = 0;
      for (var i = 0; i < group.children.length; i++) {
        var child = group.children[i];
        if (child instanceof HTMLElement) height += child.offsetHeight;
      }

      var headerHeight = this.offsetHeight;
      if (headerHeight !== this.lastHeaderHeight) {
        this.lastHeaderHeight = headerHeight;
        document.body.style.setProperty('--header-height', headerHeight + 'px');
      }
      if (height !== this.lastGroupHeight) {
        this.lastGroupHeight = height;
        document.body.style.setProperty('--header-group-height', height + 'px');
      }

      // Recorded only while the band is open, so the condense point is
      // measured against the full-height header.
      if (!this.stuck && height) this.baseGroupHeight = height;
    }
  }

  customElements.define('rtb-header', RtbHeader);
})();
